---
title: 'Reflections on Epic Web Conf'
slug: reflections-on-epic-web-conf
date: 2024-05-02T09:00:00.002Z
opener:
  'I received an unexpected message on a dark evening in late January of this
  year.'
featured: /epic-web-social.png
---

export const meta = () => [
	{ title: `${frontmatter.title} - Barry McGee` },
	{
		name: 'description',
		content: frontmatter.opener,
	},
	{ property: 'og:title', content: `${frontmatter.title} - Barry McGee` },
	{
		property: 'og:description',
		content: frontmatter.opener,
	},
	{ property: 'og:image', content: 'https://barrymcgee.dev/img/speaking.jpeg' },
	{ property: 'og:type', content: 'article' },
	{
		property: 'og:url',
		content: `https://barrymcgee.dev/notes/${frontmatter.slug}`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:site', content: '@barrymcgee' },
	{ name: 'twitter:creator', content: '@barrymcgee' },
]

# {frontmatter.title}

<div className="opener">{frontmatter.opener}</div>

<img
	src="/img/epic-web.jpg"
	alt="Giving my talk on stage at Epic Web Conference"
/>

<a href="https://kentcdodds.com/">Kent C. Dodds</a> is the co-founder of Remix, founder
of React Testing Library and all-round titan of our industry and he had just sent
me a private message on Twitter.

One week prior to this message from Kent, I had tweeted him a somewhat
tongue-in-cheek reply to let him know in jest that my team at Shop had beaten
him to punch in migrating our Remix app to Vite a day earlier than he had.

What I didn't realize when pinging off this tweet is that I just inadvertently
joined two dots in virtual space by putting a name and a face to the team
working on Shop with Remix in Shopify and that a week later Kent would invite me
to speak at his inaugural <a href="https://www.epicweb.dev/conf">Epic Web
Conf</a> in Park City, Utah, USA - the spiritual successor to Remix Conf.

I'd never spoken at a big conference before. The high water mark of my external
public speaking accolades on work related topics was previously the level of a
Javascript meetup in Belfast. This was a very exciting opportunity - an all
expenses paid jaunt to The States, a chance to meet many cool fellow speakers, a
chance to meet Kent and a chance to elevate my profile within the industry. Not
to mention the promise of swag! However.. it was daunting - not because I didn't
feel I could compile a compelling slide deck but mainly because -

<a href="/notes/2024-03-24-stuttering/">I stutter</a>. Public speaking is a self
confessed challenge for most people. It's a particular challenge for anyone with
a speech impediment as it tends to very publicly amplify that impediment.

I often find myself having this same internal conflict - I stutter so I should
avoid the high level of discomfort that might come from agreeing to speak but
also I am a person who stutters and I _must not_ let that dictate where and when
I speak. The urge to take the easy way out is strong but I'm also stubborn to a
fault so the latter argument wins more often than not. I thanked Kent for the
opportunity, got the requisite approval to speak of behalf of Shopify and my
name was inked on the speaker slate.

## The prep

I had two months to craft a twenty minute talk of how we built and shipped
https://shop.app using Remix with a dash of AI.

A twenty minute talk initially sounded like a breeze until I sat down and
realized how much talking there actually is in twenty minutes. 😅

> I found the biggest challenge when prepping the talk was providing an
> appropriately pitched narrative with a fulfilling conclusion for attendees to
> take away.

When I thought about conference talks I had enjoyed in the past, they all had a
mix of insight, relatability and validating what I already knew. They also had a
satisfying story arc and left me with something I thought I could take away and
apply to my own work. These guidelines provided my success criteria.

The second biggest challenge was providing enough insight and interest to
attendees who don't work at Shopify and don't know the product while staying
within the bounds of what can be communicated publicly. When you're speaking on
behalf of a publicly traded company, there are many topics that are off-limits
and numbers of any nature are particularly sensitive. I wanted to provide enough
insight that attendees would feel they had a good peek behind the proverbial
curtain without revealing any secret sauce as dictated by the terms of my
employment - I hope I managed to achieve that.

> In terms of managing my fluency, preparation is key. Managing fluency is not
> possible if you're also trying to wing your content.

In terms of managing my fluency, preparation is key. Managing fluency is not
possible if you're also trying to wing your content. I needed to know all what I
needed to say by just glancing at each slide so I made sure I knew them
backwards. For the two weeks or so before the trip, I would also run through the
whole talk with near perfect fluency and record it in a Voice Memo which I could
then listen back to while walking the dog. This ensured I knew my cadence, my
inflection points, which words and phrases might trip me up and how I could
counter that on stage.

## The trip

The conference was held in Park City, about an hour to the east of Salt Lake
City. It was a bit of a trek for me as I had to travel down to Dublin to fly 8
hours to Chicago, lay over there for 6 hours before onward to Salt Lake City
where I then picked up a Lyft for the final hour transfer to Park City. All in
all, it was 24 hours from my Irish bed to my American bed but I was kid-free and
buzzing for the trip so it was all good.

> I have to admit however, I was in no way prepared for the scenic beauty of
> Utah.

I have to admit however, I was in no way prepared for the scenic beauty of Utah.
I initially expected scrub and desert. Instead, I got snow capped mountains,
expansive vistas and valleys that seem to go on forever. Park City itself is an
old mining town and they've worked hard to keep the aesthetic. As a result, it's
has a stunning Main Street of brightly coloured boutiques, bars and restaurants
that will easily transport you back in time if squint just a little. It's also
7000 feet above sea level so the mountain air is crisp and the skies were clear
and blue for my entire trip. I had my first day free to beat the jet lag and
explore which I very much enjoyed.

## The conf

I was the first speaker on the slate so kicked things off on the Wednesday
evening, which was also streamed live on YouTube. Despite knowing this would
happen and coaching myself against it for weeks, I started off too fast but
thankfully I caught myself quickly and settled into it. Would I have like my
fluency to have been better? Yes. However, on balance, I'm happy enough with how
I managed that complexity while keeping a good cadence to the flow of the talk.

The main essence of my talk is to give background and context on how Shop, which
started as a mobile native only product, transitioned to the web using Remix
while integrating AI. I then outline my five key takeaways which I believe
allowed us to ship with confidence and at scale in time for Black Friday late
last year.

<div className="callout">
	<img src="/img/epic-web-logo.svg" alt="Epic Web Conf logo" />
	<p>
		▶️ Watch my talk:
		<br /> <a href="https://www.epicweb.dev/talks/building-shop-app-at-scale-with-remix-and-ai">
			Building shop.app at scale with Remix and AI
		</a>
	</p>
</div>

Once my talk was over, I had the pleasure of sitting back and enjoying the
absolutely stellar line up of talks offered by everyone else. I <a target="_blank" href="https://twitter.com/barrymcgee/status/1778449996083568765">kept a tweet stream going throughout the day</a> to highlight each talk. However, even better, every talk has now been chopped up, professionally edited and published free-to-all on the Epic Web Conf site - <a href="https://www.epicweb.dev/talks">go check 'em all</a>, every single one of them was interesting. 👏🏻

> Being a speaker at a conference also means you are very well looked after.

Being a speaker at a conference also means you are very well looked after. Shout
out to Brianne and her crew who couldn't do enough to make sure every
accommodation was made to make myself and the other speakers feel like proper
VIPs.

## The afters

Everyone at the conference was super friendly and I met loads of cool people in
the conference dining rooms and the hallway track. I found having a speaker
lanyard was also a really great way to break the ice as it was often the first
thing people would mention.

Special mentions also go to <a href="https://x.com/devneill
">Dev</a>, <a href="https://x.com/BrooksLybrand">Brooks</a>,

<a href="https://x.com/djfarrelly">Dan</a> and
<a href="https://x.com/simonswiss">Simon</a> with whom I covered much ground in the
hotel bar.🍻

The day after the conference, lots of folks headed for the ski slopes. I've
never skied and figured I wouldn't get much value from half a day in isolation
so instead I rented a gravel bike and headed down the valley. I had clocked the <a href="https://www.visitutah.com/articles/a-biking-guide-to-historic-union-pacific-trail">Historic Union Pacific Rail Trail</a> the day after I arrived and had made a mental note.

I had the bike for the whole day and was well prepped with fluids, snacks and
sunscreen so decided to head to the next town north along the trail which was
Wanship. I was keenly aware I was traveling downhill with the wind at my back
heading towards Wanship but the gradient was reasonably gentle so I decided to
push on. The scenery was absolutely stunning.

<img src="/img/bike.jpg" alt="Biking the Union Pacific Rail Trail" />

It took me about 60 minutes to make it down to Wanship where I reached meadows
with horses grazing and a really sleepy little town with a single service
station and a really old junkyard. The return trip was slightly more work than I
anticipated as it took me nearly 2 hours 30 minutes to make it back up the
valley to Park City but I kept it steady and made it back feeling very
accomplished and ready for a beer.

I did however get a bit of a jolt upon relaying my day's adventure to the lady
behind the hotel bar when she expressed some surprise I had biked alone due to
the risk of roaming cougars. 😱 I wasn't sure of how serious she was as she did
have a glint in her eye telling me this but I've since did the research and
while it's uncommon to see roaming mountain lions, it's certainly a non-zero
risk.

I'd been cognizant spelling of not wandering into grassy areas due to the risk
of snakes but had never once had I considered the risk of a big cat.

Anyway - I made it home in one piece and lived to tell the tale. A really epic
trip all round and I'm very grateful to have had the opportunity. Until next
time Utah!
